import { mappingRows, renderPreviewLines, truncated } from './wechat_template_preview.js'

export default Vue.extend({
  props: {
    item: {
      type: Object,
      required: true
    },
    wechatTemplates: {
      type: Array,
      default() {
        return []
      }
    },
    wechatTitle: {
      type: String,
      default: ''
    },
    dirty: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    hasWechatTemplates() {
      return this.wechatTemplates.length > 0
    },
    templateOptions() {
      const options = this.wechatTemplates.slice()
      if (
        this.item.template_id &&
        !options.some((template) => template.template_id === this.item.template_id)
      ) {
        options.unshift({
          template_id: this.item.template_id,
          title: this.item.template_id,
          content: '',
          params: []
        })
      }
      return options
    },
    selectedWechatTemplate() {
      return this.wechatTemplates.find((template) => template.template_id === this.item.template_id) || null
    },
    paramLabels() {
      const labels = {}
      if (!this.selectedWechatTemplate) return labels
      this.selectedWechatTemplate.params.forEach((param) => {
        labels[param.key] = param.label
      })
      return labels
    },
    previewLines() {
      if (!this.selectedWechatTemplate) return null
      return renderPreviewLines(
        this.selectedWechatTemplate.content,
        this.item.template_fields,
        this.item.sources
      )
    },
    previewRows() {
      return mappingRows(this.item.template_fields, this.item.sources)
    },
    hasPreview() {
      return !!(this.previewLines || this.previewRows.length)
    },
    previewTitle() {
      if (!this.wechatTitle) return this.item.title
      return `${this.item.title} · ${this.wechatTitle}`
    },
    previewEmptyHint() {
      if (!this.item.template_id && !this.hasWechatTemplates) {
        return '先在上方拉取模板，或直接填写模板 ID'
      }
      if (!this.item.template_id) {
        return '请选择一个微信模板'
      }
      return '选择模板并完成映射后，这里会显示样例效果。'
    }
  },
  methods: {
    selectWechatTemplate(event) {
      const templateId = event.target.value
      if (!templateId) {
        this.item.template_id = ''
        return
      }
      this.$emit('select-template', this.item, templateId)
    },
    patchRow(index, patch) {
      const rows = this.item.template_fields.slice()
      rows[index] = Object.assign({}, rows[index], patch)
      this.item.template_fields = rows
    },
    addRow() {
      this.item.template_fields = this.item.template_fields.concat([{ key: '', source: '' }])
    },
    removeRow(index) {
      this.item.template_fields = this.item.template_fields.filter((_row, position) => position !== index)
    },
    isTruncated(row) {
      return truncated(row, this.item.sources)
    },
    isUnmapped(row) {
      return !!(row.key && row.key.trim() !== '' && !row.source)
    }
  },
  template: `
    <section class="push-template-card">
      <div class="push-template-card-body">
        <div class="push-template-config">
          <div class="push-template-field">
            <label class="push-template-label">模板</label>
            <select
              v-if="hasWechatTemplates"
              class="push-template-control"
              :value="item.template_id"
              @change="selectWechatTemplate"
            >
              <option value="">请选择微信模板</option>
              <option
                v-for="template in templateOptions"
                :key="template.template_id"
                :value="template.template_id"
              >
                {{ template.title }}
              </option>
            </select>
            <input
              v-else
              class="push-template-control"
              placeholder="先在上方拉取模板，或直接填写模板 ID"
              v-model.trim="item.template_id"
              type="text"
            >
          </div>

          <div class="push-template-field">
            <div class="push-template-label-row">
              <label class="push-template-label">字段映射</label>
              <button
                v-if="!selectedWechatTemplate"
                type="button"
                class="push-template-text-btn"
                @click="addRow"
              >添加一行</button>
            </div>

            <div
              v-if="item.template_fields.length"
              class="push-template-mapping"
              :class="{ 'is-manual': !selectedWechatTemplate }"
            >
              <div class="push-template-mapping-head">
                <span>模板参数</span>
                <span>业务字段</span>
                <span v-if="!selectedWechatTemplate"></span>
              </div>
              <div
                v-for="(row, index) in item.template_fields"
                :key="index"
                class="push-template-mapping-row"
                :class="{ 'is-unmapped': isUnmapped(row) }"
              >
                <div class="push-template-param" v-if="selectedWechatTemplate">
                  <span class="push-template-param-key">{{ row.key }}</span>
                  <span v-if="paramLabels[row.key]" class="push-template-param-label">{{ paramLabels[row.key] }}</span>
                </div>
                <input
                  v-else
                  class="push-template-control"
                  placeholder="如 thing1"
                  :value="row.key"
                  @input="patchRow(index, { key: $event.target.value })"
                >
                <select
                  class="push-template-control"
                  :value="row.source"
                  @change="patchRow(index, { source: $event.target.value })"
                >
                  <option value="">请选择</option>
                  <option v-for="source in item.sources" :key="source.key" :value="source.key">
                    {{ source.label }}
                  </option>
                </select>
                <button
                  v-if="!selectedWechatTemplate"
                  type="button"
                  class="push-template-text-btn is-danger"
                  @click="removeRow(index)"
                >删除</button>
                <p v-if="isTruncated(row)" class="push-template-warning">该字段有字数限制，样例值会被截断。</p>
              </div>
            </div>
            <p v-else class="push-template-hint">选择模板后会自动带出参数；未拉取时也可手动添加。</p>
          </div>
        </div>

        <aside class="push-template-preview">
          <label class="push-template-label">预览</label>
          <div class="push-template-preview-card" :class="{ 'is-empty': !hasPreview }">
            <p v-if="!hasPreview" class="push-template-hint">{{ previewEmptyHint }}</p>
            <template v-else>
              <p class="push-template-preview-title">{{ previewTitle }}</p>
              <template v-if="previewLines">
                <p v-for="(line, lineIndex) in previewLines" :key="lineIndex" class="push-template-preview-line">
                  <span v-if="line.label" class="push-template-preview-key">{{ line.label }}</span>
                  <span>
                    <span
                      v-for="(segment, segmentIndex) in line.segments"
                      :key="segmentIndex"
                      :class="{
                        'push-template-unmapped': segment.kind === 'unmapped',
                        'push-template-value': segment.kind === 'value'
                      }"
                    >{{ segment.text }}</span>
                  </span>
                </p>
              </template>
              <template v-else>
                <p v-for="row in previewRows" :key="row.key" class="push-template-preview-line">
                  <span class="push-template-preview-key">{{ row.key }}</span>
                  <span v-if="row.sample">{{ row.sample }}</span>
                  <span v-else class="push-template-unmapped">未设置</span>
                </p>
              </template>
              <p class="push-template-preview-detail">查看详情</p>
            </template>
          </div>
        </aside>
      </div>

      <footer class="push-template-card-foot">
        <button
          type="button"
          class="btn btn-default"
          :disabled="item._disabled"
          @click="$emit('test-push', item)"
        >测试推送</button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="item._disabled || !dirty"
          @click="$emit('update', item)"
        >{{ dirty ? '更新' : '已保存' }}</button>
      </footer>
    </section>
  `
})
